import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { DEFAULT_BRANDING_SETTINGS, type BrandingSettings, type BrandingTheme } from "@rwaforms/shared";
import { createTheme, deleteTheme, fetchTeamThemes, updateTheme } from "../lib/api-client";

function TeamThemesPage(): JSX.Element {
  const { teamId } = useParams<{ teamId: string }>();
  const queryClient = useQueryClient();

  const [selectedThemeId, setSelectedThemeId] = useState<string | null>(null);
  const [draftName, setDraftName] = useState("");
  const [draftSettings, setDraftSettings] = useState<BrandingSettings>(DEFAULT_BRANDING_SETTINGS);

  const themesQuery = useQuery({
    queryKey: ["team-themes", teamId],
    queryFn: () => fetchTeamThemes(teamId!),
    enabled: Boolean(teamId),
  });

  const themes = themesQuery.data?.themes ?? [];
  const activeThemeId = themesQuery.data?.activeThemeId ?? null;

  useEffect(() => {
    if (!themes.length || selectedThemeId) return;
    const first = themes[0];
    if (first) {
      setSelectedThemeId(first.id);
      setDraftName(first.name);
      setDraftSettings(cloneSettings(first.settings));
    }
  }, [themes, selectedThemeId]);

  const selectedTheme = useMemo<BrandingTheme | null>(() => {
    if (!selectedThemeId) return null;
    return themes.find((theme) => theme.id === selectedThemeId) ?? null;
  }, [themes, selectedThemeId]);

  useEffect(() => {
    if (!selectedTheme) return;
    setDraftName(selectedTheme.name);
    setDraftSettings(cloneSettings(selectedTheme.settings));
  }, [selectedTheme]);

  const createThemeMutation = useMutation({
    mutationFn: async () => {
      if (!teamId) throw new Error("teamId missing");
      return createTheme(teamId, {
        name: generateThemeName(themes),
        settings: DEFAULT_BRANDING_SETTINGS,
      });
    },
    onSuccess(response) {
      void queryClient.invalidateQueries({ queryKey: ["team-themes", teamId] });
      setSelectedThemeId(response.theme.id);
      setDraftName(response.theme.name);
      setDraftSettings(cloneSettings(response.theme.settings));
    },
  });

  const updateThemeMutation = useMutation({
    mutationFn: async (payload: { setDefault?: boolean }) => {
      if (!teamId || !selectedThemeId) throw new Error("theme not selected");
      return updateTheme(teamId, selectedThemeId, {
        name: draftName.trim(),
        settings: draftSettings,
        setDefault: payload.setDefault,
      });
    },
    onSuccess() {
      void queryClient.invalidateQueries({ queryKey: ["team-themes", teamId] });
    },
  });

  const deleteThemeMutation = useMutation({
    mutationFn: async () => {
      if (!teamId || !selectedThemeId) throw new Error("theme not selected");
      await deleteTheme(teamId, selectedThemeId);
    },
    onSuccess() {
      void queryClient.invalidateQueries({ queryKey: ["team-themes", teamId] });
      setSelectedThemeId(null);
      setDraftName("");
      setDraftSettings(DEFAULT_BRANDING_SETTINGS);
    },
  });

  const handleSelectTheme = (theme: BrandingTheme) => {
    setSelectedThemeId(theme.id);
    setDraftName(theme.name);
    setDraftSettings(cloneSettings(theme.settings));
  };

  return (
    <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      <header
        style={{
          padding: "1.5rem 2rem",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid var(--color-border)",
          background: "var(--color-surface)",
        }}
      >
        <div>
          <h1 style={{ fontSize: "1.5rem", margin: 0 }}>Theme Studio</h1>
          <p style={{ margin: 0, color: "var(--color-text-muted)" }}>
            Manage reusable branding presets for embedded and public forms.
          </p>
        </div>
        <button
          type="button"
          className="button primary"
          onClick={() => createThemeMutation.mutate()}
          disabled={createThemeMutation.isPending}
        >
          {createThemeMutation.isPending ? "Creating…" : "New theme"}
        </button>
      </header>

      <main style={{ padding: "2rem", display: "grid", gap: "2rem", gridTemplateColumns: "320px minmax(0, 1fr)", flex: 1 }}>
        <aside className="surface scroll-container" style={{ padding: "1rem", maxHeight: "70vh" }}>
          <h2 style={{ margin: "0 0 0.5rem" }}>Available themes</h2>
          {themesQuery.isLoading ? (
            <p style={{ color: "var(--color-text-muted)" }}>Loading themes…</p>
          ) : themes.length === 0 ? (
            <p style={{ color: "var(--color-text-muted)" }}>No themes yet. Create one to get started.</p>
          ) : (
            <div style={{ display: "grid", gap: "0.75rem" }}>
              {themes.map((theme) => (
                <button
                  key={theme.id}
                  type="button"
                  onClick={() => handleSelectTheme(theme)}
                  style={{
                    border: theme.id === selectedThemeId ? "2px solid var(--color-primary)" : "1px solid var(--color-border)",
                    borderRadius: "var(--radius-base)",
                    padding: "0.75rem",
                    textAlign: "left",
                    background: "var(--color-surface)",
                    cursor: "pointer",
                    display: "grid",
                    gap: "0.5rem",
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span style={{ fontWeight: 600 }}>{theme.name}</span>
                    {theme.id === activeThemeId && (
                      <span className="pill" style={{ fontSize: "0.7rem" }}>
                        Default
                      </span>
                    )}
                  </div>
                  <ThemePalettePreview settings={theme.settings} />
                </button>
              ))}
            </div>
          )}
        </aside>

        <section className="surface" style={{ padding: "1.5rem", display: "grid", gap: "1.25rem" }}>
          {selectedTheme ? (
            <>
              <header>
                <h2 style={{ margin: "0 0 0.4rem" }}>{selectedTheme.name}</h2>
                <p style={{ margin: 0, color: "var(--color-text-muted)" }}>
                  Update colors, typography, and border radii to match your brand.
                </p>
              </header>
              <ThemeEditor
                draftName={draftName}
                setDraftName={setDraftName}
                draftSettings={draftSettings}
                setDraftSettings={setDraftSettings}
              />
              <footer style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap" }}>
                <button
                  type="button"
                  className="button primary"
                  onClick={() => updateThemeMutation.mutate({})}
                  disabled={updateThemeMutation.isPending}
                >
                  {updateThemeMutation.isPending ? "Saving…" : "Save changes"}
                </button>
                <button
                  type="button"
                  className="button ghost"
                  onClick={() => updateThemeMutation.mutate({ setDefault: true })}
                  disabled={updateThemeMutation.isPending || selectedTheme.id === activeThemeId}
                >
                  Set as default
                </button>
                <button
                  type="button"
                  className="button ghost"
                  style={{ color: "var(--color-critical)", borderColor: "var(--color-border)" }}
                  onClick={() => deleteThemeMutation.mutate()}
                  disabled={deleteThemeMutation.isPending}
                >
                  Delete
                </button>
              </footer>
            </>
          ) : (
            <div style={{ color: "var(--color-text-muted)" }}>Select a theme to edit its settings.</div>
          )}
        </section>
      </main>
    </div>
  );
}

function ThemeEditor({
  draftName,
  setDraftName,
  draftSettings,
  setDraftSettings,
}: {
  draftName: string;
  setDraftName(value: string): void;
  draftSettings: BrandingSettings;
  setDraftSettings(settings: BrandingSettings): void;
}): JSX.Element {
  const updatePalette = (key: keyof BrandingSettings["palette"], value: string) => {
    setDraftSettings({
      ...draftSettings,
      palette: {
        ...draftSettings.palette,
        [key]: value,
      },
    });
  };

  const updateTypography = (key: keyof BrandingSettings["typography"], value: string | number) => {
    setDraftSettings({
      ...draftSettings,
      typography: {
        ...draftSettings.typography,
        [key]: typeof value === "number" ? value : Number.parseInt(value, 10),
      },
    });
  };

  return (
    <div style={{ display: "grid", gap: "1rem" }}>
      <label style={labelStyle}>
        <span>Name</span>
        <input
          value={draftName}
          onChange={(event) => setDraftName(event.target.value)}
          style={inputStyle}
          placeholder="Theme name"
        />
      </label>
      <div style={{ display: "grid", gap: "0.75rem" }}>
        <h3 style={{ margin: 0 }}>Palette</h3>
        <div style={{ display: "grid", gap: "0.75rem", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))" }}>
          <ColorInput label="Primary" value={draftSettings.palette.primary} onChange={(value) => updatePalette("primary", value)} />
          <ColorInput label="Accent" value={draftSettings.palette.accent} onChange={(value) => updatePalette("accent", value)} />
          <ColorInput label="Surface" value={draftSettings.palette.surface} onChange={(value) => updatePalette("surface", value)} />
          <ColorInput label="Text" value={draftSettings.palette.text} onChange={(value) => updatePalette("text", value)} />
        </div>
      </div>
      <div style={{ display: "grid", gap: "0.75rem" }}>
        <h3 style={{ margin: 0 }}>Typography</h3>
        <label style={labelStyle}>
          <span>Font family</span>
          <input
            value={draftSettings.typography.fontFamily}
            onChange={(event) => updateTypography("fontFamily", event.target.value)}
            style={inputStyle}
            placeholder="Inter, 'Segoe UI', sans-serif"
          />
        </label>
        <div style={{ display: "grid", gap: "0.75rem", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))" }}>
          <label style={labelStyle}>
            <span>Heading weight</span>
            <input
              type="number"
              min={100}
              max={900}
              step={100}
              value={draftSettings.typography.headingWeight}
              onChange={(event) => updateTypography("headingWeight", event.target.value)}
              style={inputStyle}
            />
          </label>
          <label style={labelStyle}>
            <span>Body weight</span>
            <input
              type="number"
              min={100}
              max={900}
              step={100}
              value={draftSettings.typography.bodyWeight}
              onChange={(event) => updateTypography("bodyWeight", event.target.value)}
              style={inputStyle}
            />
          </label>
        </div>
      </div>
      <label style={labelStyle}>
        <span>Border radius</span>
        <input
          type="number"
          min={0}
          max={32}
          value={draftSettings.borderRadius}
          onChange={(event) =>
            setDraftSettings({
              ...draftSettings,
              borderRadius: Number.parseInt(event.target.value, 10) || 0,
            })
          }
          style={inputStyle}
        />
      </label>
      <ThemePalettePreview settings={draftSettings} />
    </div>
  );
}

function ColorInput({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange(value: string): void;
}): JSX.Element {
  const normalized = value.startsWith("#") ? value : `#${value}`;
  return (
    <label style={labelStyle}>
      <span>{label}</span>
      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
        <input
          type="color"
          value={normalized}
          onChange={(event) => onChange(event.target.value)}
          style={{ width: "2.5rem", height: "2.5rem", border: "none", background: "transparent" }}
        />
        <input
          value={normalized}
          onChange={(event) => onChange(event.target.value)}
          style={{ ...inputStyle, flex: 1 }}
        />
      </div>
    </label>
  );
}

function ThemePalettePreview({ settings }: { settings: BrandingSettings }): JSX.Element {
  return (
    <div
      style={{
        display: "grid",
        gap: "0.5rem",
        padding: "0.75rem",
        borderRadius: "var(--radius-base)",
        border: "1px solid var(--color-border)",
        background: settings.palette.surface,
        color: settings.palette.text,
        fontFamily: settings.typography.fontFamily,
        transition: "all 0.2s ease",
      }}
    >
      <div style={{ display: "flex", gap: "0.5rem" }}>
        <Swatch color={settings.palette.primary} label="Primary" />
        <Swatch color={settings.palette.accent} label="Accent" />
        <Swatch color={settings.palette.surface} label="Surface" />
        <Swatch color={settings.palette.text} label="Text" />
      </div>
      <div>
        <h4 style={{ margin: 0, fontWeight: settings.typography.headingWeight }}>Heading text</h4>
        <p style={{ margin: 0, fontSize: "0.85rem", fontWeight: settings.typography.bodyWeight }}>
          Body text preview with border radius {settings.borderRadius}px.
        </p>
      </div>
    </div>
  );
}

function Swatch({ color, label }: { color: string; label: string }): JSX.Element {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem", alignItems: "center" }}>
      <span
        style={{
          width: "1.7rem",
          height: "1.7rem",
          borderRadius: "999px",
          border: "1px solid rgba(0,0,0,0.08)",
          background: color,
        }}
      />
      <span style={{ fontSize: "0.7rem", color: "var(--color-text-muted)" }}>{label}</span>
    </div>
  );
}

const labelStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "0.35rem",
  fontSize: "0.85rem",
};

const inputStyle: React.CSSProperties = {
  borderRadius: "10px",
  border: "1px solid var(--color-border)",
  padding: "0.55rem 0.75rem",
  fontSize: "0.9rem",
  outline: "none",
};

function cloneSettings(settings: BrandingSettings): BrandingSettings {
  return JSON.parse(JSON.stringify(settings)) as BrandingSettings;
}

function generateThemeName(existing: BrandingTheme[]): string {
  const base = "New theme";
  if (!existing.some((theme) => theme.name === base)) {
    return base;
  }
  let index = 2;
  while (existing.some((theme) => theme.name === `${base} ${index}`)) {
    index += 1;
  }
  return `${base} ${index}`;
}

export default TeamThemesPage;
